/**
 * Client for the admin monitoring API (/api/admin/monitoring).
 *
 * Read-only views over the Sprint 1 observability tables:
 *   GET /runs            — list ingest processing runs
 *   GET /runs/{id}       — one run: steps + the model calls it made
 *   GET /summary         — pipeline health aggregate (runs by status, failed steps)
 *   GET /model-runs      — list individual model calls
 *   GET /model-eval      — per (task, provider, model) operational metrics
 *
 * Admin-only on the backend (require_admin); the page is also AdminRoute-gated.
 */

import { apiClient } from './api'

export interface StepSummary {
  total: number
  pending: number
  running: number
  completed: number
  failed: number
  skipped: number
}

export interface ProcessingRun {
  id: string
  asset_id: number
  asset_filename: string | null
  status: string
  started_at: string | null
  completed_at: string | null
  error_message: string | null
  created_at: string
  duration_s: number | null
  steps: StepSummary
}

export interface ProcessingRunList {
  total: number
  runs: ProcessingRun[]
}

export interface ProcessingStep {
  id: string
  step_name: string
  status: string
  started_at: string | null
  completed_at: string | null
  duration_s: number | null
  error_message: string | null
  attempt_count: number
  source_service: string | null
  model_name: string | null
  model_version: string | null
  // What the step had to repair in the model's output, e.g.
  // { shots_salvaged: 3 }. Null when it repaired nothing.
  repairs: Record<string, number> | null
}

export interface ModelRun {
  id: string
  asset_id: number | null
  processing_run_id: string | null
  task_type: string | null
  provider: string | null
  model_name: string | null
  model_version: string | null
  prompt_version: string | null
  latency_ms: number | null
  token_input_count: number | null
  token_output_count: number | null
  cost_estimate: number | null
  status: string
  error_message: string | null
  created_at: string
}

export interface RunDetail {
  run: ProcessingRun
  steps: ProcessingStep[]
  model_runs: ModelRun[]
}

export interface HealthSummary {
  days: number
  total_runs: number
  by_status: Record<string, number>
  failed_steps: Record<string, number>
}

export interface ModelEvalRow {
  task_type: string | null
  provider: string | null
  model_name: string | null
  count: number
  success_count: number
  failure_count: number
  error_rate: number
  avg_latency_ms: number | null
  avg_input_tokens: number | null
  avg_output_tokens: number | null
  total_input_tokens: number
  total_output_tokens: number
  total_cost_estimate: number | null
}

export interface ModelEvalResponse {
  days: number
  rows: ModelEvalRow[]
}

// Product quality, as distinct from the operational health above: what
// editors did to the drafts the model proposed. Every field is nullable
// because "no data yet" and "a rate of zero" are very different answers.
export interface AcceptanceSummary {
  proposed: number
  excluded: number
  locked: number
  acceptance_rate: number | null
  lock_rate: number | null
}

export interface ScoreCalibrationRow {
  bucket: number
  score_from: number
  score_to: number
  proposed: number
  excluded: number
  exclusion_rate: number | null
}

export interface RegenerationSummary {
  assemblies_created: number
  sections_regenerated: number
  regenerations_per_assembly: number | null
}

export interface OrderChurnSummary {
  clips_compared: number
  moved_count: number | null
  moved_rate: number | null
  rank_correlation: number | null
}

export interface EditorFeedbackResponse {
  days: number
  acceptance: AcceptanceSummary
  score_calibration: ScoreCalibrationRow[]
  regeneration: RegenerationSummary
  order_churn: OrderChurnSummary
}

export interface RunFilters {
  status?: string
  asset_id?: number
  days?: number
  limit?: number
  offset?: number
}

const BASE = '/admin/monitoring'

export const monitoringApi = {
  async listRuns(params: RunFilters = {}): Promise<ProcessingRunList> {
    const res = await apiClient.get<ProcessingRunList>(`${BASE}/runs`, { params })
    return res.data
  },

  async getRun(runId: string): Promise<RunDetail> {
    const res = await apiClient.get<RunDetail>(`${BASE}/runs/${runId}`)
    return res.data
  },

  async summary(days = 7): Promise<HealthSummary> {
    const res = await apiClient.get<HealthSummary>(`${BASE}/summary`, { params: { days } })
    return res.data
  },

  async modelEval(params: { days?: number; task_type?: string } = {}): Promise<ModelEvalResponse> {
    const res = await apiClient.get<ModelEvalResponse>(`${BASE}/model-eval`, { params })
    return res.data
  },

  async editorFeedback(params: { days?: number; buckets?: number } = {}): Promise<EditorFeedbackResponse> {
    const res = await apiClient.get<EditorFeedbackResponse>(`${BASE}/editor-feedback`, { params })
    return res.data
  },
}
